import { useEffect, useRef } from 'react';
import { useProject } from '@contexts/Project.context';
import { objectLabel } from '../utils/transformUtils';

/**
 * Converts a hex color string (3 or 6 digit) to the { r, g, b, a } object
 * format used by the slider's convertToRgba helper.
 *
 * @param {string} hex - e.g. '#3366ff' or '#abc'
 * @returns {{ r: number, g: number, b: number, a: number }}
 */
const hexToColorObject = hex => {
	if (!hex) return { r: 255, g: 0, b: 0, a: 1 };
	let full = hex;
	const shortMatch = hex.match(/^#([0-9a-f]{3})$/i);
	if (shortMatch) {
		const [r, g, b] = shortMatch[1].split('');
		full = `#${r}${r}${g}${g}${b}${b}`;
	}
	return {
		r: parseInt(full.slice(1, 3), 16),
		g: parseInt(full.slice(3, 5), 16),
		b: parseInt(full.slice(5, 7), 16),
		a: 1,
	};
};

/**
 * Marks grid-point OL Features with a `detections` property based on the
 * collection's normalized features, so the existing styleFunction renders
 * image points green when they contain detections.
 *
 * Sets `feature.set('detections', { features, classes })` on each grid feature
 * whose `name` matches a `tile_name` in the normalized dataset, and clears it
 * on features with no matching detections. This is the same property contract
 * that the model-based flow (matchGeoJsonToFeatures) uses, so the existing
 * styleFunction reads it without changes.
 *
 * In DEFAULT app mode, each `classes` entry includes a `color` field (as an
 * { r, g, b, a } object) resolved from the collection object's hex color,
 * so the slider's DetectionHits can render colored dots without needing the
 * model-based colorScheme.
 *
 * Runs as a side-effect whenever the normalized features or filtered images
 * change. Cleans up on unmount by clearing all markers.
 *
 * @param {Array} normalizedFeatures - collection features in normalized coords
 * @param {Array<{key: string, color?: string}>} objects - collection objects (for class metadata)
 * @param {boolean} enabled - only run for single-image projects with data
 */
const useCollectionImageMarkers = (normalizedFeatures, objects, enabled) => {
	const { singleImageFeatures, dispatch } = useProject();
	const markedRef = useRef(new Set());
	const singleImageFeaturesRef = useRef(singleImageFeatures);
	singleImageFeaturesRef.current = singleImageFeatures;

	useEffect(() => {
		if (!enabled || !singleImageFeatures?.length) return;

		// Pre-build color lookup from collection objects
		const colorByKey = new Map(
			(objects ?? []).map(obj => [obj.key, hexToColorObject(obj.color)])
		);

		// Group normalized features by tile_name
		const byTile = new Map();
		for (const feature of normalizedFeatures) {
			const tileName = feature.properties?.tile_name;
			if (!tileName) continue;
			if (!byTile.has(tileName)) byTile.set(tileName, []);
			byTile.get(tileName).push(feature);
		}

		const newMarked = new Set();

		for (const gridFeature of singleImageFeatures) {
			const name = gridFeature.get('name');
			const matching = byTile.get(name);

			if (matching?.length) {
				// Build classes summary per node_uuid
				const classCounts = {};
				for (const item of matching) {
					const key = item.properties?.node_uuid;
					if (!key) continue;
					if (!classCounts[key]) {
						classCounts[key] = {
							// The display name; Filter Images matches on `id`, as names can repeat.
							name: objectLabel(objects, key),
							id: key,
							count: 0,
							color: colorByKey.get(key) ?? { r: 255, g: 0, b: 0, a: 1 },
						};
					}
					classCounts[key].count += 1;
				}

				// Intentionally omits `features` — only `classes` is set so that:
				//   • styleFunction sees a truthy value → green point
				//   • DetectionHits in the slider reads .classes → colored dots + counts
				//   • SelectedSingleImageLayer's renderPolygonsOnLayer guards on
				//     `data?.features?.length` and skips rendering (the per-image
				//     overlay is handled by CollectionSingleImageLayer instead)
				gridFeature.set('detections', {
					classes: Object.values(classCounts),
				});
				newMarked.add(name);
			} else if (markedRef.current.has(name)) {
				// Was previously marked — clear it
				gridFeature.set('detections', null);
			}
		}

		// Clear any previously marked features that are no longer in the set
		for (const prevName of markedRef.current) {
			if (!newMarked.has(prevName)) {
				const gridFeature = singleImageFeatures.find(f => f.get('name') === prevName);
				if (gridFeature) gridFeature.set('detections', null);
			}
		}

		// OL Feature property mutations don't trigger React re-renders. Dispatch
		// setFilteredImages so the slider re-reads `item.get('detections')` and
		// shows the updated color dots / counts. Same pattern as the model-based
		// flow's updateDetections in SingleImagePointsLayer.
		//
		// The effect only re-runs when `normalizedFeatures` or `objects` change
		// (i.e. collection switch or new data), so dispatching whenever there are
		// marked tiles — or the set changed (e.g. clearing previous markers) —
		// is safe and ensures the slider reflects the current state.
		const prevMarked = markedRef.current;
		const markedSetChanged =
			newMarked.size !== prevMarked.size ||
			[...newMarked].some(name => !prevMarked.has(name));

		markedRef.current = newMarked;

		if (markedSetChanged || newMarked.size > 0) {
			// Spread into a new array to ensure React sees a new reference and
			// re-renders the slider — dispatching the same array instance that's
			// already in state would be a no-op for React's reconciliation.
			dispatch({ type: 'setFilteredImages', payload: [...singleImageFeatures] });
		}
	}, [enabled, normalizedFeatures, objects, singleImageFeatures]);

	// Cleanup on unmount — clear all markers
	useEffect(() => {
		return () => {
			const features = singleImageFeaturesRef.current;
			if (!features?.length) return;
			for (const name of markedRef.current) {
				const gridFeature = features.find(f => f.get('name') === name);
				if (gridFeature) gridFeature.set('detections', null);
			}
			markedRef.current = new Set();
		};
	}, []);
};

export default useCollectionImageMarkers;
